import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Pencil } from "lucide-react";

const emptyForm = { name: "", description: "", location: "", date: "", active: true };

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/events");
      setEvents(data);
    } catch { toast.error("Could not load events."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      name: ev.name || "", description: ev.description || "",
      location: ev.location || "", date: ev.date || "", active: !!ev.active,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Event name is required."); return; }
    try {
      if (editingId) {
        await api.patch(`/events/${editingId}`, form);
        toast.success("Event updated.");
      } else {
        await api.post("/events", form);
        toast.success("Event created.");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save event.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event? Existing records will remain.")) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success("Event deleted.");
      load();
    } catch { toast.error("Could not delete event."); }
  };

  const toggleActive = async (ev) => {
    try {
      await api.patch(`/events/${ev.id}`, { active: !ev.active });
      load();
    } catch { toast.error("Could not update event."); }
  };

  return (
    <div className="max-w-6xl" data-testid="events-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="overline">Manage</p>
          <h1 className="font-heading text-4xl font-light tracking-tight mt-2">Events</h1>
          <p className="text-brand-muted font-body mt-2">Only active events appear on the public time clock.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              onClick={openCreate}
              data-testid="create-event-button"
              className="h-11 px-5 rounded-md bg-brand-primary text-white font-body font-medium flex items-center gap-2 transition-colors hover:bg-brand-primaryHover"
            >
              <Plus className="w-4 h-4" /> New event
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white sm:max-w-lg" data-testid="event-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading font-light text-2xl">
                {editingId ? "Edit event" : "Create event"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="overline block mb-2">Name</Label>
                <Input
                  data-testid="event-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Community Cleanup — Saturday"
                  className="bg-white"
                />
              </div>
              <div>
                <Label className="overline block mb-2">Location</Label>
                <Input
                  data-testid="event-location-input"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Riverside Park"
                  className="bg-white"
                />
              </div>
              <div>
                <Label className="overline block mb-2">Date / time</Label>
                <Input
                  data-testid="event-date-input"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  placeholder="Sat, Mar 8 · 9am–1pm"
                  className="bg-white"
                />
              </div>
              <div>
                <Label className="overline block mb-2">Description</Label>
                <Textarea
                  data-testid="event-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-white"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-brand-border p-4">
                <div>
                  <p className="font-body font-medium">Active</p>
                  <p className="text-xs text-brand-muted">Volunteers can pick this event.</p>
                </div>
                <Switch
                  data-testid="event-active-switch"
                  checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <button
                onClick={() => setOpen(false)}
                data-testid="event-cancel-button"
                className="h-10 px-4 rounded-md border border-brand-border font-body transition-colors hover:bg-brand-bg"
              >Cancel</button>
              <button
                onClick={save}
                data-testid="event-save-button"
                className="h-10 px-5 rounded-md bg-brand-primary text-white font-body font-medium transition-colors hover:bg-brand-primaryHover"
              >Save</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 bg-white border border-brand-border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-brand-bg">
              <TableHead>Event</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="text-brand-muted">Loading…</TableCell></TableRow>
            )}
            {!loading && events.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-brand-muted py-10 text-center" data-testid="events-empty">
                  No events yet. Create one to get started.
                </TableCell>
              </TableRow>
            )}
            {events.map((ev) => (
              <TableRow key={ev.id} data-testid={`event-row-${ev.id}`}>
                <TableCell>
                  <p className="font-body font-medium">{ev.name}</p>
                  {ev.description && <p className="text-xs text-brand-muted mt-1 line-clamp-1">{ev.description}</p>}
                </TableCell>
                <TableCell className="text-brand-muted">{ev.location || "—"}</TableCell>
                <TableCell className="text-brand-muted">{ev.date || "—"}</TableCell>
                <TableCell>
                  <button
                    onClick={() => toggleActive(ev)}
                    data-testid={`toggle-active-${ev.id}`}
                    className={`text-xs font-body px-2.5 py-1 rounded-full border transition-colors ${
                      ev.active
                        ? "bg-brand-primary/10 border-brand-primary/30 text-brand-primary"
                        : "bg-brand-bg border-brand-border text-brand-muted"
                    }`}
                  >
                    {ev.active ? "Active" : "Inactive"}
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-2">
                    <button
                      onClick={() => openEdit(ev)}
                      data-testid={`edit-event-${ev.id}`}
                      className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-brand-border transition-colors hover:bg-brand-bg"
                    ><Pencil className="w-4 h-4" /></button>
                    <button
                      onClick={() => remove(ev.id)}
                      data-testid={`delete-event-${ev.id}`}
                      className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-brand-border text-brand-warm transition-colors hover:bg-brand-bg"
                    ><Trash2 className="w-4 h-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
